import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Link, Stack, Tooltip, Typography } from "@mui/material";
import {
  type GridColDef,
  type GridColumnOrderChangeParams,
  type GridRenderCellParams,
  type GridSortModel,
} from "@mui/x-data-grid-premium";
import type {
  ColumnSorting,
  DimensionDescriptor,
  DrillDownColumn,
  DrillDownRow,
} from "../../../../../../../../../api/types/svTypes.ts";
import { CellPropertyEnum, ItemDataType } from "../../../../../../../../../api/types/svTypes.ts";
import type { FieldWithOrder, FieldWithSorting } from "../../../../../hooks/FieldWithOrder";
import { sortOrderedFields } from "../../../../../hooks/sortOrderedFields";
import DrillDownAttachedFilesIcon from "../DrillDownAttachedFilesIcon";

export const PAGE_SIZE = 500;
export const HYPOTHETICAL_ATTACHED_FILES_COLUMN_NAME = "TransactionNo";

export const rebuildColumnsSorting = (model: GridSortModel, allFields: DimensionDescriptor[]) => {
  const allFieldsCopy = [...allFields];
  const newSorting = model
    .map((sort, index) => {
      const field = allFieldsCopy.find((f) => f.name === sort.field);
      if (field) {
        const sortField = {
          field,
          order: index,
          sortAsc: sort.sort === "asc",
        } as FieldWithSorting;
        return sortField;
      }
      return undefined;
    })
    .filter((field): field is FieldWithSorting => !!field);
  return newSorting;
};

export const rebuildColumnsOrder = (params: GridColumnOrderChangeParams, fields: FieldWithOrder[]) => {
  const arr = [...fields];
  const item = arr.splice(params.oldIndex, 1)[0];

  if (!item) {
    return arr;
  }
  arr.splice(params.targetIndex, 0, item);

  return arr.map((f, index) => {
    return { ...f, order: index };
  });
};

export const HIGHLIGHTED_COLUMN_CLASS = "dd-highlighted-col";

const buildRenderDrillDownCell = (onOpenAttachedFilesDialog: (transactionNo: string) => void) => {
  return function renderDrillDownCell(params: GridRenderCellParams<DrillDownRow>) {
    const properties = params.row.cells?.[params.field]?.properties;
    const link = properties?.[CellPropertyEnum.Link];
    const fileNames = properties?.[CellPropertyEnum.FileNames];

    const value = link ? (
      <Link
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        onClick={(event) => event.stopPropagation()}
      >
        {params.value}
      </Link>
    ) : (
      params.value
    );

    if (!fileNames) {
      return value;
    }

    const transactionNo = String(params.row.cells?.["TransactionNo"]?.value ?? "");

    return (
      <Stack direction="row" sx={{ alignItems: "center", gap: 0.5, width: "100%" }}>
        <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {value}
        </Box>
        <DrillDownAttachedFilesIcon
          fileNames={fileNames}
          transactionNo={transactionNo}
          onOpenDialog={onOpenAttachedFilesDialog}
        />
      </Stack>
    );
  };
};

export const createColumns = (
  fields: FieldWithOrder[],
  highlightedColumns: string[] | undefined,
  onOpenAttachedFilesDialog: (transactionNo: string) => void
): GridColDef[] => {
  const arr = [...fields];
  const highlighted = new Set(highlightedColumns ?? []);
  const renderDrillDownCell = buildRenderDrillDownCell(onOpenAttachedFilesDialog);
  return arr.sort(sortOrderedFields).map((f) => {
    const isHighlighted = highlighted.has(f.field.name);
    return {
      field: f.field.name,
      valueGetter: (value, row: DrillDownRow, column: GridColDef) => {
        // Data rows carry a cell per field; the aggregation footer row does not, so fall
        // back to the value already resolved by the data source's getAggregatedValue.
        const cell = row.cells?.[column.field];
        return cell ? cell.formattedValue || cell.value : value;
      },
      renderCell: renderDrillDownCell,
      headerName: f.field.caption,
      width: f.field.name === HYPOTHETICAL_ATTACHED_FILES_COLUMN_NAME ? 180 : 150,
      align: f.field.type === ItemDataType.Numeric ? "right" : "left",
      aggregable: isHighlighted,
      hideable: false,
      pinnable: false,
      groupable: false,
      filterable: false,
      headerClassName: isHighlighted ? HIGHLIGHTED_COLUMN_CLASS : undefined,
      cellClassName: isHighlighted ? HIGHLIGHTED_COLUMN_CLASS : undefined,
      renderHeader: isHighlighted
        ? ({ colDef }) => (
            <Stack direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
              <Tooltip title="Source column of the calculated value">
                <InfoOutlinedIcon sx={{ color: "text.secondary" }} />
              </Tooltip>
              <Typography noWrap component="span" variant="caption" sx={{ fontWeight: 500 }}>
                {colDef.headerName}
              </Typography>
            </Stack>
          )
        : undefined,
    };
  });
};

export const shouldSkipFetch = (newSkip: number, newTake: number, prevSkip: number, prevTake: number) => {
  const emptyRequest = newSkip === 0 && newTake === 0;
  const sameRequest = prevSkip === newSkip && prevTake === newTake;

  // Check if the new request is within the range of the previous request
  const withinPreviousRange = newSkip >= prevSkip && newSkip + newTake <= prevSkip + prevTake;

  return emptyRequest || sameRequest || withinPreviousRange;
};

export const getDrillDownColumns = (fields: FieldWithOrder[]): DrillDownColumn[] => {
  return fields.map((f) => {
    const col: DrillDownColumn = {
      dimensionName: f.field.name,
    };
    return col;
  });
};

export const getSorting = (fields: FieldWithSorting[]): ColumnSorting[] => {
  return fields.map((f) => {
    const col: ColumnSorting = {
      dimensionName: f.field.name,
      sortAsc: f.sortAsc,
      caption: f.field.caption,
    };
    return col;
  });
};
